import { useCallback, useRef } from 'react';

// Cache for prefetched URLs to avoid duplicate requests
const prefetchedUrls = new Set<string>();

export const usePrefetch = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const prefetch = useCallback((url: string) => {
    // Don't prefetch if already done
    if (prefetchedUrls.has(url)) return;

    // Clear any pending prefetch
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Delay prefetch slightly to avoid prefetching on quick mouse movements
    timeoutRef.current = setTimeout(() => {
      if (prefetchedUrls.has(url)) return;

      // Create a prefetch link
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);

      prefetchedUrls.add(url);
    }, 100);
  }, []);

  const cancelPrefetch = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { prefetch, cancelPrefetch };
};

// Simple component wrapper for prefetching article links
export const prefetchArticle = (slug: string) => {
  const url = `/article/${slug}/`;
  if (prefetchedUrls.has(url)) return;

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  link.as = 'document';
  document.head.appendChild(link);

  prefetchedUrls.add(url);
};

export default usePrefetch;
